import { FC, KeyboardEvent, useCallback, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';

import { provide, useDependencies } from '@servicetitan/react-ioc';
import { Flex, Text, Icon } from '@servicetitan/anvil2';
import { AnvilSelect } from '@servicetitan/design-system';
import { PartitionsClientApi } from '@servicetitan/marketing-services-api/identity';
import OfficeIcon from '@servicetitan/anvil2/assets/icons/material/round/location_city.svg';
import ExpandLessIcon from '@servicetitan/anvil2/assets/icons/material/round/expand_less.svg';
import ExpandMoreIcon from '@servicetitan/anvil2/assets/icons/material/round/expand_more.svg';

import { PartitionsStore } from '../../stores/partitions.store';
import { SAFE_REDIRECT_PATH } from '../../enums/routes';
import { SAFE_REDIRECT_URL_PARAM } from '../safe-redirect/safe-redirect';

import * as Styles from './partitions-dropdown.module.less';
import * as HeaderStyles from './header.module.less';

export const PartitionsDropdown: FC = provide({
    singletons: [PartitionsStore, PartitionsClientApi],
})(
    observer(() => {
        const [{ partition, partitions, partitionName, pickPartition }] =
            useDependencies(PartitionsStore);
        const [isOpen, setIsOpen] = useState(false);
        const [filter, setFilter] = useState('');
        const history = useHistory();

        const handlerToggle = useCallback(
            (open: boolean) => {
                setIsOpen(open);
            },
            [setIsOpen],
        );

        const emptyMessage = 'No options found';

        const onSearchChange = useCallback(
            ({ target }: KeyboardEvent<HTMLInputElement>) => {
                setFilter((target as HTMLInputElement).value);
            },
            [setFilter],
        );

        const options = useMemo(() => {
            const opts = partitions.map(partition => ({
                value: partition.internal_id,
                label: partition.display_name,
            }));

            if (filter) {
                return opts.filter(({ label }) =>
                    label.toLowerCase().includes(filter.toLowerCase()),
                );
            }

            return opts;
        }, [partitions, filter]);

        const handlePickPartition = useCallback(
            ({ value }: { value: string }) => {
                pickPartition(value, uri =>
                    history.push(
                        `${SAFE_REDIRECT_PATH}?${SAFE_REDIRECT_URL_PARAM}=${encodeURIComponent(
                            uri,
                        )}`,
                    ),
                );
            },
            [history, pickPartition],
        );

        if (!partitionName) {
            return null;
        }

        const trigger = (
            <Flex
                className={classNames(HeaderStyles.navItem, { [HeaderStyles.active]: isOpen })}
                alignItems="center"
                direction="row"
                gap="1"
            >
                <Icon svg={OfficeIcon} size="large" />
                <Text className="c-white">{partitionName}</Text>
                <Icon svg={isOpen ? ExpandLessIcon : ExpandMoreIcon} />
            </Flex>
        );

        return (
            <AnvilSelect
                trigger={trigger}
                onOpenChange={handlerToggle}
                portal
                popoverWidth="auto"
                open={isOpen}
                search={{
                    search: true,
                    className: Styles.search,
                    placeholder: 'Search Accounts',
                    onChange: onSearchChange,
                }}
                className={Styles.popover}
                emptyResult={emptyMessage}
                value={partition}
                onChange={handlePickPartition}
                options={options}
            />
        );
    }),
);
